/* ============================================================================
   Contact — a professional contact experience.
   ----------------------------------------------------------------------------
   The submit handler, the API call, the field names, the honeypot and the
   error/success handling are unchanged. What is rebuilt is everything around
   them: a compact hero instead of a full marketing one, the routing options
   beside the form rather than above it, and a form that looks like a real
   product form.

   The routing panel is deliberately first in the reading order on narrow
   viewports: most people arriving here with a tracking token want the claim
   tracker, not this form, and telling them so saves them a round trip.
   ========================================================================== */
import { useState, type FormEvent } from 'react';
import { ApiError, type ApiClient } from '@enshield/api-client';
import { Button, Field, Input, Notice, Select, Textarea } from '@enshield/ui';
import { PageMeta } from '../components/PageMeta';
import { Band, Plate, ActionLink } from '../site/primitives';
import { Icon } from '../site/icons';
import { AstronautArt, Hero } from '../site/sections';
import { ART } from '../site/media';

export function ContactPage({ apiClient }: { apiClient: ApiClient }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('general');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSent(false);
    try {
      await apiClient.post('/public/contact', { name: name.trim(), email: email.trim(), topic, message: message.trim(), website });
      setSent(true);
      setMessage('');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'We could not receive your message. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageMeta title="Contact Enshield" description="Contact Enshield about claim links, product support, or merchant integrations." />

      <Hero compact titleId="contact-title" eyebrow="Contact" title="How can we help?" lead="Reach out to our team and we’ll follow up." art={<AstronautArt {...ART.reading} support priority className="astra-support-guide" />} />

      <Band space="standard" flushTop>
        <div className="p-contact">
          {/*
            Routing options.

            Keep this column task-focused. Decorative artwork is intentionally
            omitted so the security guidance follows the routing options
            without creating a dead zone beside the compact form.
          */}
          <aside className="p-contact__aside u-stack u-stack--lg" aria-label="Other ways to get help">
            <div className="u-card u-stack u-stack--sm">
              <Plate><Icon name="shield-check" /></Plate>
              <h2 className="u-h4">Have a tracking token?</h2>
              <p className="u-small">The fastest way to see a claim update or add evidence is the claim tracker.</p>
              <ActionLink to="/track-claim">Track a claim</ActionLink>
            </div>

            <div className="u-card u-stack u-stack--sm">
              <Plate><Icon name="clipboard" /></Plate>
              <h2 className="u-h4">Need to file a claim?</h2>
              <p className="u-small">Claims start with the store you bought from. We can help you find the right link.</p>
              <ActionLink to="/file-a-claim">File a claim</ActionLink>
            </div>

            <div className="u-card u-stack u-stack--sm">
              <Plate><Icon name="mail" /></Plate>
              <h2 className="u-h4">Prefer email?</h2>
              <p className="u-small">
                Write to <a href="mailto:support@enshield.com">support@enshield.com</a> and we’ll route it internally.
              </p>
            </div>

            {/* Kept and made prominent: it is a security instruction, not a
                footnote. */}
            <p className="u-small p-secure">
              <Icon name="lock" aria-hidden="true" />
              Never include passwords, payment-card details, or a private claim tracking token in a message.
            </p>
          </aside>

          {/* The form. */}
          <form className="p-contact__form u-card" onSubmit={submit}>
            <div className="u-stack u-stack--sm">
              <h2 className="u-h3">Send a message</h2>
              <p className="u-small">All fields are required.</p>
            </div>

            {error ? <Notice tone="error">{error}</Notice> : null}
            {sent ? <Notice tone="success">Your message was received. If a reply is needed, the team will respond by email.</Notice> : null}

            <div className="p-contact__row">
              <Field label="Name" required>
                <Input value={name} onChange={(e) => setName(e.currentTarget.value)} maxLength={120} autoComplete="name" />
              </Field>
              <Field label="Email" required>
                <Input type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} maxLength={255} autoComplete="email" />
              </Field>
            </div>

            <Field label="Topic" required>
              <Select value={topic} onChange={(e) => setTopic(e.currentTarget.value)}>
                <option value="general">General question</option>
                <option value="claims">Claim link or tracking</option>
                <option value="merchant">Merchant integrations</option>
                <option value="privacy">Privacy request</option>
              </Select>
            </Field>

            <Field label="Message" required>
              <Textarea value={message} onChange={(e) => setMessage(e.currentTarget.value)} minLength={10} maxLength={4000} />
            </Field>

            {/* Honeypot. Unchanged. */}
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="website">Company website</label>
              <input id="website" name="website" value={website} onChange={(e) => setWebsite(e.currentTarget.value)} tabIndex={-1} autoComplete="off" />
            </div>

            <Button type="submit" busy={busy}>Send message</Button>
          </form>
        </div>
      </Band>
    </>
  );
}
